/**
 * WeatherUploadForm - Weather data upload component for forecasting.
 *
 * Allows users to upload CSV weather data (GHI, temperature, wind speed, cloud cover)
 * to improve generation forecast accuracy.
 *
 * Features:
 *   - File upload with CSV validation
 *   - Preview of first 5 rows
 *   - Expected format guidance
 *   - Weather type selector (Solar / Wind)
 *
 * Props:
 *   - onClose: Callback when form is closed
 *   - onUpload: Callback when file is uploaded (optional)
 */

import React, { useState } from 'react'
import { Card, Form, Button, Alert, Table } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'

function WeatherUploadForm({ onClose, onUpload }) {
  const { t } = useTranslation()
  const [selectedFile, setSelectedFile] = useState(null)
  const [weatherType, setWeatherType] = useState('solar')
  const [previewData, setPreviewData] = useState([])
  const [uploadSuccess, setUploadSuccess] = useState(false)

  // Handle file selection and parse CSV preview
  const handleFileChange = (event) => {
    const file = event.target.files[0]
    if (!file) return

    setSelectedFile(file)
    setUploadSuccess(false)

    // Read first few lines for preview
    const reader = new FileReader()
    reader.onload = (e) => {
      const text = e.target.result
      const lines = text.split('\n').slice(0, 6) // Header + 5 rows
      const rows = lines.map(line => line.split(',').map(cell => cell.trim()))
      setPreviewData(rows)
    }
    reader.readAsText(file)
  }

  // Handle upload (mock implementation)
  const handleUpload = () => {
    // TODO: POST to /api/datasets with kind: 'weather'
    setUploadSuccess(true)
    setTimeout(() => {
      if (onUpload) onUpload()
      if (onClose) onClose()
    }, 1500)
  }

  // Expected format description
  const expectedFormat = weatherType === 'solar'
    ? {
        title: t('weather_upload.solar_format_title'),
        required: ['timestamp', 'ghi_w_m2'],
        optional: ['temperature_c', 'cloud_cover_pct', 'dni_w_m2'],
        example: 'timestamp,ghi_w_m2,temperature_c\n2026-02-08T00:00:00Z,0,5\n2026-02-08T01:00:00Z,0,4',
      }
    : {
        title: t('weather_upload.wind_format_title'),
        required: ['timestamp', 'wind_speed_m_s'],
        optional: ['wind_direction_deg', 'temperature_c'],
        example: 'timestamp,wind_speed_m_s,wind_direction_deg\n2026-02-08T00:00:00Z,5.2,220\n2026-02-08T01:00:00Z,6.8,240',
      }

  return (
    <Card style={{ border: '1px solid var(--color-primary-light)', marginTop: 'var(--space-3)' }}>
      <Card.Body>
        <Card.Title style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, color: 'var(--color-primary-dark)' }}>
          {t('weather_upload.title')}
        </Card.Title>

        <Alert variant="info" style={{ fontSize: 'var(--font-size-xs)', padding: 'var(--space-2) var(--space-3)' }}>
          <strong>{t('weather_upload.tip')}</strong> {t('weather_upload.tip_text')}
        </Alert>

        {/* Weather Type Selector */}
        <Form.Group className="mb-3">
          <Form.Label style={{ fontWeight: 500, fontSize: 'var(--font-size-sm)' }}>{t('weather_upload.weather_type')}</Form.Label>
          <div className="d-flex gap-3">
            <Form.Check
              type="radio"
              id="weather-solar"
              label={t('weather_upload.solar_label')}
              value="solar"
              checked={weatherType === 'solar'}
              onChange={(e) => setWeatherType(e.target.value)}
            />
            <Form.Check
              type="radio"
              id="weather-wind"
              label={t('weather_upload.wind_label')}
              value="wind"
              checked={weatherType === 'wind'}
              onChange={(e) => setWeatherType(e.target.value)}
            />
          </div>
        </Form.Group>

        {/* File Upload */}
        <Form.Group className="mb-3">
          <Form.Label style={{ fontWeight: 500, fontSize: 'var(--font-size-sm)' }}>{t('weather_upload.csv_file')}</Form.Label>
          <Form.Control
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            style={{ fontSize: 'var(--font-size-sm)' }}
          />
          <Form.Text className="text-muted">
            {t('weather_upload.csv_help')}
          </Form.Text>
        </Form.Group>

        {/* Expected Format */}
        <div className="mb-3" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)' }}>
          <strong>{expectedFormat.title}</strong>
          <div className="mt-1">
            <strong>{t('weather_upload.required_label')}</strong> {expectedFormat.required.join(', ')}
          </div>
          <div>
            <strong>{t('weather_upload.optional_label')}</strong> {expectedFormat.optional.join(', ')}
          </div>
          <pre
            style={{
              marginTop: 'var(--space-2)',
              padding: 'var(--space-2)',
              backgroundColor: 'var(--color-neutral-100)',
              borderRadius: 'var(--radius-sm)',
              fontSize: '10px',
              overflow: 'auto',
            }}
          >
            {expectedFormat.example}
          </pre>
        </div>

        {/* Preview Table */}
        {previewData.length > 0 && (
          <div className="mb-3">
            <strong style={{ fontSize: 'var(--font-size-sm)' }}>{t('weather_upload.preview_title')}</strong>
            <div style={{ maxHeight: '180px', overflowY: 'auto', marginTop: 'var(--space-2)' }}>
              <Table
                bordered
                size="sm"
                style={{ fontSize: 'var(--font-size-xs)', marginBottom: 0 }}
              >
                <thead style={{ backgroundColor: 'var(--color-neutral-100)' }}>
                  <tr>
                    {previewData[0]?.map((header, i) => (
                      <th key={i}>{header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewData.slice(1).map((row, i) => (
                    <tr key={i}>
                      {row.map((cell, j) => (
                        <td key={j}>{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        )}

        {/* Success Message */}
        {uploadSuccess && (
          <Alert variant="success" style={{ fontSize: 'var(--font-size-sm)', padding: 'var(--space-2) var(--space-3)' }}>
            {t('weather_upload.upload_success')}
          </Alert>
        )}

        {/* Actions */}
        <div className="d-flex gap-2">
          <Button
            size="sm"
            variant="primary"
            onClick={handleUpload}
            disabled={!selectedFile || uploadSuccess}
            style={{ backgroundColor: 'var(--color-primary)', borderColor: 'var(--color-primary)' }}
          >
            {uploadSuccess ? t('weather_upload.btn_uploaded') : t('weather_upload.btn_upload')}
          </Button>
          <Button size="sm" variant="outline-secondary" onClick={onClose}>
            {uploadSuccess ? t('weather_upload.btn_close') : t('weather_upload.btn_cancel')}
          </Button>
        </div>
      </Card.Body>
    </Card>
  )
}

export default WeatherUploadForm
